import { Alignment, Color, DynamicLoader, getThemeColor, JSUtil, LogManager, StringUtil } from "@mcleod/core";
import { Component, Panel } from "../..";
import { AnchorMutation, AnchorProps } from "../Anchor";

export interface AnchoredComponentPositionerProps {
    anchorProps: AnchorProps;
    anchoredComponent: Component;
    showPointer?: boolean;
    pointerColor?: Color | ((position: Alignment) => Color)
}

export enum AnchoredComponentType {
    TOOLTIP = "tooltip",
    OVERLAY = "overlay"
}

const positionerClassPath = "components/page/positioner/floating-ui/FloatingUIPositioner";

const log = LogManager.getLogger("components.page.positioner.AnchoredComponentPositioner");
const POINTER_SIZE = 7;

/**
 * Abstract base class for positioning anchored components (e.g., tooltips, overlays) relative to an anchor element.
 * Subclasses must implement `listenForPositionUpdates` and `computePosition` to define the positioning logic.
 */
export abstract class AnchoredComponentPositioner implements AnchoredComponentPositionerProps {
    componentType: AnchoredComponentType;
    anchorProps: AnchorProps;
    anchoredComponent: Component;
    pointer: Panel;
    showPointer: boolean;
    pointerColor?: Color | ((position: Alignment) => Color);
    protected currentMutation: AnchorMutation;
    private debouncedOnMutate: (elem: HTMLElement, mutation: AnchorMutation) => void;

    protected constructor(props: AnchoredComponentPositionerProps, componentType: AnchoredComponentType) {
        log.debug("Creating positioner for the", componentType, "component anchored to", props.anchorProps.anchor);

        Object.assign(this, props);
        this.componentType = componentType;

        if (typeof this.anchorProps.onMutate === "function") {
            this.debouncedOnMutate = JSUtil.debounce((elem: HTMLElement, mutation: AnchorMutation) => {
                this.anchorProps.onMutate(elem, mutation)
            }, 100);
        }

        if (StringUtil.isEmptyString(this.anchoredComponent.style.width))
            this.anchoredComponent.style.width = "max-content";
        this.anchoredComponent.style.position = "absolute";

        this.anchorProps.position ??= this.getDefaultPosition();
        this.currentMutation = { newRect: this.componentElement.getBoundingClientRect(), newAnchor: {...this.anchorProps} };
        log.debug("Initial anchor position:", this.anchorProps.position, "for anchor element:", this.anchorElement);

        if (this.showPointer === true) {
            this.addPointer();
        }
    }

    abstract listenForPositionUpdates(): void;
    abstract computePosition(): Promise<AnchorMutation>;

    get isTooltipType(): boolean {
        return this.componentType === AnchoredComponentType.TOOLTIP;
    }

    get isOverlayType(): boolean {
        return this.componentType === AnchoredComponentType.OVERLAY;
    }

    protected get pointerSize(): number {
        return this.pointer != null ? POINTER_SIZE : 0;
    }

    protected get anchorElement() : HTMLElement{
        const anchor = this.anchorProps.anchor;
        if ( anchor instanceof Component) {
            if (this.isTooltipType && anchor.tooltipAnchor != null)
                return anchor.tooltipAnchor;
            return anchor._element;
        }
        return anchor;
    }

    get componentElement() : HTMLElement{
        return this.anchoredComponent._element;
    }

    private addPointer() {
        this.pointer = new Panel({ borderWidth: this.pointerSize, padding: 0, rowBreak: false, backgroundColor: "transparent" });
        this.pointer.style.position = "absolute";
        this.pointer.style.border = "none";
        this.anchoredComponent._element.appendChild(this.pointer._element);
    }

    async computeAndUpdatePosition() {
        log.debug("Computing position for component anchored to", this.anchorElement,"with initial position:", this.anchorProps.position);
        this.currentMutation = await this.computePosition();
        log.debug("Computed new position:", this.currentMutation.newAnchor.position, "at coordinates:", { x: this.currentMutation.newRect.x, y: this.currentMutation.newRect.y });
        this.updatePosition();
    }

    protected updatePosition() {
        requestAnimationFrame(() => {
            const {x, y} = this.currentMutation.newRect;
            log.debug("Updating component position to x:", x, "y:", y);

            this.anchoredComponent.setStyleProps({
                left: `${x}px`,
                top: `${y}px`
            });
            this.adjustPointer();
            this.debouncedOnMutate?.(this.componentElement, this.currentMutation);
        });
    }

    protected adjustPointer(): void {
        if (this.pointer == null)
            return;

        const previouslySetStyles: Partial<CSSStyleDeclaration> = this.pointer["__adjustedStyles"] || {};
        for (const key in previouslySetStyles) {
            this.pointer.style[key] = "";
        }

        const style = this.getAdjustedPointerStyle();
        this.pointer.setStyleProps(style);
        this.pointer["__adjustedStyles"] = style;
    }

    protected getAdjustedPointerStyle(): Partial<CSSStyleDeclaration> {

        const position = this.currentMutation.newAnchor.position;
        const color = this.determinePointerColor(position);
        const pointerSize = this.pointerSize;

        switch (position) {
            case Alignment.TOP:
                return {
                    borderLeft: `${pointerSize}px solid transparent`,
                    borderRight: `${pointerSize}px solid transparent`,
                    borderTop: `${pointerSize}px solid ${color}`,
                    width: `${pointerSize * 2}px`,
                    height: `${pointerSize}px`,
                }
            case Alignment.BOTTOM:
                return {
                    borderLeft: `${pointerSize}px solid transparent`,
                    borderRight: `${pointerSize}px solid transparent`,
                    borderBottom: `${pointerSize}px solid ${color}`,
                    width: `${pointerSize * 2}px`,
                    height: `${pointerSize}px`,
                }
            case Alignment.LEFT:
                return {
                    borderTop: `${pointerSize}px solid transparent`,
                    borderBottom: `${pointerSize}px solid transparent`,
                    borderLeft: `${pointerSize}px solid ${color}`,
                    width: `${pointerSize}px`,
                    height: `${pointerSize * 2}px`,
                }
            case Alignment.RIGHT:
                return {
                    borderTop: `${pointerSize}px solid transparent`,
                    borderBottom: `${pointerSize}px solid transparent`,
                    borderRight: `${pointerSize}px solid ${color}`,
                    width: `${pointerSize}px`,
                    height: `${pointerSize * 2}px`,
                }
        }
    }

    protected determinePointerColor(position: Alignment): Color {
        let result = "";
        if (typeof this.pointerColor === "function") {
            result = this.pointerColor(position);
        } else {
            result = this.pointerColor
        }
        if (StringUtil.isEmptyString(result))
            return "transparent";

        return getThemeColor(result);
    }

    getDefaultPosition(): Alignment {
        return Alignment.BOTTOM;
    }

    public static async autoUpdateTooltipPosition(props: AnchoredComponentPositionerProps): Promise<AnchoredComponentPositioner> {
        return this.autoUpdatePosition(props, AnchoredComponentType.TOOLTIP);
    }

    public static async autoUpdateOverlayPosition(props: AnchoredComponentPositionerProps): Promise<AnchoredComponentPositioner>{
        return this.autoUpdatePosition(props, AnchoredComponentType.OVERLAY);
    }

    private static async autoUpdatePosition(props: AnchoredComponentPositionerProps, compType: AnchoredComponentType): Promise<AnchoredComponentPositioner>{
        const positionerClass = DynamicLoader.getClassForPath(positionerClassPath);
        const positioner: AnchoredComponentPositioner = new positionerClass(props, compType);
        await positioner.computeAndUpdatePosition();
        positioner.listenForPositionUpdates();
        return positioner;
    }
}
